import React, { useState, useEffect } from 'react';

const API_URL = 'https://tormal-api.winter-mouse-2fc0.workers.dev';

function Pricing({ prices }) {
  const [tiers, setTiers] = useState([]);
  const [selectedCrypto, setSelectedCrypto] = useState('btc');

  useEffect(() => {
    fetchTiers();
  }, []);

  const fetchTiers = async () => {
    try {
      const res = await fetch(`${API_URL}/api/subscriptions/tiers`);
      const data = await res.json();
      setTiers(data.tiers || []);
    } catch (e) {
      console.error('Failed to fetch tiers');
    }
  };

  const handleUpgrade = async (tier) => {
    alert(`Upgrade to ${tier.display_name} selected!\n\nIn production, this would:\n1. Create a crypto invoice\n2. Show QR code for payment\n3. Activate subscription after confirmation\n\nPayment address would be shown for: ${selectedCrypto.toUpperCase()}`);
  };

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>Choose Your Plan</h1>
      <p style={styles.subtitle}>Secure anonymous email with crypto payments</p>

      {/* Live Prices */}
      <div style={styles.priceTicker}>
        <span style={styles.tickerLabel}>Live Prices:</span>
        {prices.bitcoin && (
          <>
            <span style={{...styles.cryptoPrice, color: '#f7931a'}}>
              BTC ${prices.bitcoin.usd?.toLocaleString()}
            </span>
            <span style={{...styles.cryptoPrice, color: '#627eea'}}>
              ETH ${prices.ethereum?.usd?.toLocaleString()}
            </span>
            <span style={{...styles.cryptoPrice, color: '#9945ff'}}>
              SOL ${prices.solana?.usd?.toLocaleString()}
            </span>
          </>
        )}
      </div>

      {/* Crypto Selector */}
      <div style={styles.cryptoSelector}>
        <span>Pay with:</span>
        {['btc', 'eth', 'sol'].map(crypto => (
          <button
            key={crypto}
            style={{
              ...styles.cryptoBtn,
              ...(selectedCrypto === crypto ? styles.cryptoBtnActive : {})
            }}
            onClick={() => setSelectedCrypto(crypto)}
          >
            {crypto.toUpperCase()}
          </button>
        ))}
      </div>

      {/* Pricing Cards */}
      <div style={styles.grid}>
        {tiers.map(tier => (
          <div
            key={tier.name}
            style={{
              ...styles.card,
              ...(tier.recommended ? styles.cardRecommended : {})
            }}
          >
            {tier.recommended && <div style={styles.badge}>⭐ MOST POPULAR</div>}
            
            <h3 style={styles.tierName}>{tier.display_name}</h3>
            <p style={styles.tierDesc}>{tier.description}</p>

            <div style={styles.price}>
              <span style={styles.priceAmount}>${tier.prices?.usd?.monthly}</span>
              <span style={styles.pricePeriod}>/month</span>
            </div>

            <div style={styles.cryptoPriceDisplay}>
              or {tier.prices?.crypto?.[selectedCrypto]} {selectedCrypto.toUpperCase()}
            </div>

            <ul style={styles.features}>
              <li style={styles.feature}>✓ {tier.limits?.daily_send} emails/day</li>
              <li style={styles.feature}>✓ {tier.limits?.storage_mb} MB storage</li>
              <li style={styles.feature}>✓ {tier.limits?.max_accounts} email accounts</li>
              <li style={styles.feature}>✓ {tier.limits?.max_temp_aliases} temp aliases</li>
              {tier.features?.api_access && <li style={styles.feature}>✓ API access</li>}
              {tier.features?.custom_domain && <li style={styles.feature}>✓ Custom domain</li>}
              {tier.features?.pgp_encryption && <li style={styles.feature}>✓ PGP encryption</li>}
              {tier.features?.priority_support && <li style={styles.feature}>✓ Priority support</li>}
            </ul>

            <button
              style={{
                ...styles.button,
                ...(tier.recommended ? styles.buttonPrimary : {})
              }}
              onClick={() => handleUpgrade(tier)}
            >
              {tier.prices?.usd?.monthly === '0.00' ? 'Get Started Free' : 'Upgrade'}
            </button>
          </div>
        ))}
      </div>

      <div style={styles.guarantee}>
        <h3>🔒 30-Day Money-Back Guarantee</h3>
        <p>Not satisfied? Get a full refund within 30 days. No questions asked.</p>
      </div>
    </div>
  );
}

const styles = {
  container: {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '2rem',
  },
  title: {
    textAlign: 'center',
    fontSize: '2.5rem',
    marginBottom: '0.5rem',
  },
  subtitle: {
    textAlign: 'center',
    color: '#888',
    marginBottom: '2rem',
  },
  priceTicker: {
    background: 'rgba(0,0,0,0.3)',
    padding: '1rem',
    borderRadius: '12px',
    textAlign: 'center',
    marginBottom: '2rem',
    display: 'flex',
    justifyContent: 'center',
    gap: '2rem',
    flexWrap: 'wrap',
  },
  tickerLabel: {
    color: '#888',
  },
  cryptoPrice: {
    fontWeight: 'bold',
    fontFamily: 'monospace',
  },
  cryptoSelector: {
    textAlign: 'center',
    marginBottom: '2rem',
    display: 'flex',
    justifyContent: 'center',
    gap: '1rem',
    alignItems: 'center',
  },
  cryptoBtn: {
    padding: '0.8rem 1.5rem',
    background: 'rgba(255,255,255,0.1)',
    color: '#fff',
    border: '1px solid rgba(255,255,255,0.2)',
    borderRadius: '8px',
    cursor: 'pointer',
    fontWeight: 'bold',
  },
  cryptoBtnActive: {
    background: 'linear-gradient(135deg, #00d4ff, #7b2cbf)',
    borderColor: 'transparent',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: '2rem',
    marginBottom: '3rem',
  },
  card: {
    background: 'rgba(255,255,255,0.05)',
    borderRadius: '24px',
    padding: '2rem',
    border: '1px solid rgba(255,255,255,0.1)',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
  },
  cardRecommended: {
    background: 'linear-gradient(135deg, rgba(0,212,255,0.1), rgba(123,44,191,0.1))',
    border: '2px solid #00d4ff',
    transform: 'scale(1.05)',
  },
  badge: {
    position: 'absolute',
    top: '-12px',
    left: '50%',
    transform: 'translateX(-50%)',
    background: '#ffd700',
    color: '#000',
    padding: '0.4rem 1rem',
    borderRadius: '20px',
    fontSize: '0.8rem',
    fontWeight: 'bold',
  },
  tierName: {
    fontSize: '1.8rem',
    marginBottom: '0.5rem',
  },
  tierDesc: {
    color: '#888',
    fontSize: '0.95rem',
    marginBottom: '1.5rem',
  },
  price: {
    marginBottom: '0.5rem',
  },
  priceAmount: {
    fontSize: '3rem',
    fontWeight: 'bold',
  },
  pricePeriod: {
    color: '#888',
  },
  cryptoPriceDisplay: {
    color: '#888',
    fontSize: '0.9rem',
    marginBottom: '1.5rem',
  },
  features: {
    listStyle: 'none',
    marginBottom: '2rem',
    flex: 1,
  },
  feature: {
    padding: '0.5rem 0',
    borderBottom: '1px solid rgba(255,255,255,0.05)',
  },
  button: {
    width: '100%',
    padding: '1rem',
    background: 'transparent',
    color: '#fff',
    border: '2px solid rgba(255,255,255,0.3)',
    borderRadius: '12px',
    cursor: 'pointer',
    fontSize: '1.1rem',
    fontWeight: 'bold',
  },
  buttonPrimary: {
    background: '#fff',
    color: '#1a1a2e',
    border: 'none',
  },
  guarantee: {
    textAlign: 'center',
    padding: '2rem',
    background: 'rgba(0,255,0,0.05)',
    borderRadius: '16px',
    border: '1px solid rgba(0,255,0,0.2)',
  },
};

export default Pricing;

